import React, { useState, useRef, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { registrationService } from '../../../services/registrationService';
import { validatePlayerAge } from '../../../utils/playerValidation';

const PlayerRegistrationStep = ({ 
  formData, 
  errors, 
  onChange, 
  registrationId,
  onPlayerAdded,
  onPlayerRemoved 
}) => {
  const [isAddingPlayer, setIsAddingPlayer] = useState(false);
  const [isDeletingPlayer, setIsDeletingPlayer] = useState(null);
  const [isSavingPlayer, setIsSavingPlayer] = useState(null);
  const [expandedPlayers, setExpandedPlayers] = useState(new Set());
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showPlayerList, setShowPlayerList] = useState(false);
  const [playerErrors, setPlayerErrors] = useState({});
  const playerRefs = useRef({});

  const roleOptions = [
    { value: 'batter', label: 'Batter' },
    { value: 'bowler', label: 'Bowler' },
    { value: 'all_rounder', label: 'All Rounder' },
    { value: 'wicket_keeper', label: 'Wicket Keeper' }
  ];

  const tshirtSizes = [
    { value: 's_36', label: 'S - 36' },
    { value: 'm_38', label: 'M - 38' },
    { value: 'l_40', label: 'L - 40' },
    { value: 'xl_42', label: 'XL - 42' },
    { value: 'xxl_44', label: 'XXL - 44' },
    { value: 'xxxl_46', label: 'XXXL - 46' }
  ];

  const pantSizes = [
    { value: 'size_24', label: '24' },
    { value: 'size_26', label: '26' },
    { value: 'size_28', label: '28' },
    { value: 'size_30', label: '30' },
    { value: 'size_32', label: '32' },
    { value: 'size_34', label: '34' },
    { value: 'size_36', label: '36' },
    { value: 'size_38', label: '38' },
    { value: 'size_40', label: '40' }
  ];

  // Expand unsaved players by default, collapse saved ones
  useEffect(() => {
    const newExpanded = new Set();
    formData?.players?.forEach((player) => {
      if (!player?.dbId) {
        newExpanded.add(player?.id);
      }
    });
    setExpandedPlayers(newExpanded);
  }, [formData?.players?.length]); // Only when player count changes

  // Track scroll position for scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const togglePlayerExpanded = (playerId) => {
    setExpandedPlayers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(playerId)) {
        newSet.delete(playerId);
      } else {
        newSet.add(playerId);
      }
      return newSet;
    });
  };

  const scrollToPlayer = (playerId) => {
    const element = playerRefs.current[playerId];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setShowPlayerList(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  // Validate a single player
  const validatePlayer = (player) => {
    const playerErrors = {};
    
    if (!player?.firstName?.trim()) {
      playerErrors.firstName = 'First name is required';
    }
    if (!player?.lastName?.trim()) {
      playerErrors.lastName = 'Last name is required';
    }
    if (!player?.dob) {
      playerErrors.dob = 'Date of birth is required';
    } else {
      // Add age category validation
      const ageError = validatePlayerAge(player.dob, formData?.ageCategory);
      if (ageError) {
        playerErrors.dob = ageError;
      }
    }
    if (!player?.mobile?.trim()) {
      playerErrors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(player?.mobile?.trim())) {
      playerErrors.mobile = 'Invalid mobile number (must be 10 digits)';
    }
    if (!player?.email?.trim()) {
      playerErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(player?.email?.trim())) {
      playerErrors.email = 'Invalid email address';
    }
    if (!player?.role) {
      playerErrors.role = 'Player role is required';
    }
    if (!player?.tshirtSize) {
      playerErrors.tshirtSize = 'T-shirt size is required';
    }
    if (!player?.pantSize) {
      playerErrors.pantSize = 'Pant size is required';
    }
    
    return playerErrors;
  };

  // Get user-friendly validation message
  const getValidationMessage = (errors) => {
    const fields = Object.keys(errors);
    if (fields.length === 0) return null;
    
    const fieldLabels = {
      firstName: 'First Name',
      lastName: 'Last Name',
      dob: 'Date of Birth',
      mobile: 'Mobile Number',
      email: 'Email Address',
      mcaId: 'MCA ID',
      role: 'Player Role',
      tshirtSize: 'T-Shirt Size',
      pantSize: 'Pant Size'
    };
    
    const missingFields = fields
      .map(field => fieldLabels[field] || field)
      .join(', ');
    
    return `Please fill in the following required fields: ${missingFields}`;
  };

  const handleAddPlayer = () => {
    if (!registrationId) {
      alert('Please complete school details first');
      return;
    }

    // Check if there are any players and if the last player has been saved
    if (formData?.players?.length > 0) {
      const lastPlayer = formData?.players[formData?.players?.length - 1];
      
      // If last player exists but hasn't been saved (no dbId), prevent adding new player
      if (!lastPlayer?.dbId) {
        alert('Please save the current player before adding a new one.');
        return;
      }
    }

    const newPlayer = {
      id: Date.now(),
      firstName: '',
      middleName: '',
      lastName: '',
      dob: '',
      mobile: '',
      email: '',
      mcaId: '',
      role: '',
      tshirtSize: '',
      pantSize: ''
    };
    
    onChange('players', [...formData?.players, newPlayer]);
  };

  const handleRemovePlayer = async (playerId) => {
    try {
      setIsDeletingPlayer(playerId);
      
      const player = formData?.players?.find(p => p?.id === playerId);
      
      if (player?.dbId) {
        await registrationService?.deletePlayer(player?.dbId);
        onPlayerRemoved?.();
      }
      
      onChange('players', formData?.players?.filter(p => p?.id !== playerId));
      
    } catch (error) {
      console.error('Error removing player:', error);
      alert('Failed to remove player. Please try again.');
    } finally {
      setIsDeletingPlayer(null);
    }
  };

  const handlePlayerChange = (playerId, field, value) => {
    const updatedPlayers = formData?.players?.map(player =>
      player?.id === playerId ? { ...player, [field]: value } : player
    );
    onChange('players', updatedPlayers);
    
    // Clear error for this field when user starts typing
    if (playerErrors[playerId]?.[field]) {
      setPlayerErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors[playerId]) {
          delete newErrors[playerId][field];
          // Remove player from errors if no errors left
          if (Object.keys(newErrors[playerId]).length === 0) {
            delete newErrors[playerId];
          }
        }
        return newErrors;
      });
    }
  };

  const handleSavePlayer = async (playerId) => {
    if (!registrationId) return;

    const player = formData?.players?.find(p => p?.id === playerId);
    if (!player) return;

    // Validate the player
    const validationErrors = validatePlayer(player);
    
    // Store errors for this player
    setPlayerErrors(prev => ({
      ...prev,
      [playerId]: validationErrors
    }));
    
    // If there are validation errors, show user-friendly popup and scroll to first error
    if (Object.keys(validationErrors).length > 0) {
      // Build a more informative message with specific error details
      const errorMessages = Object.entries(validationErrors)
        .map(([field, message]) => {
          const fieldLabels = {
            firstName: 'First Name',
            lastName: 'Last Name',
            dob: 'Date of Birth',
            mobile: 'Mobile Number',
            email: 'Email Address',
            mcaId: 'MCA ID',
            role: 'Player Role',
            tshirtSize: 'T-Shirt Size',
            pantSize: 'Pant Size'
          };
          return `${fieldLabels[field] || field}: ${message}`;
        })
        .join('\n');
      
      alert(`Please correct the following errors:\n\n${errorMessages}`);
      
      // Scroll to the player card and expand it
      const element = playerRefs.current[playerId];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Expand the player card if collapsed
        setExpandedPlayers(prev => new Set([...prev, playerId]));
      }
      
      return;
    }
    
    // Clear errors for this player if validation passes
    setPlayerErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[playerId];
      return newErrors;
    });

    try {
      setIsSavingPlayer(playerId);
      
      const playerData = {
        firstName: player?.firstName?.trim(),
        middleName: player?.middleName?.trim() || null,
        lastName: player?.lastName?.trim(),
        dateOfBirth: player?.dob,
        mobileNumber: player?.mobile?.trim(),
        email: player?.email?.trim(),
        mcaId: player?.mcaId?.trim(),
        role: player?.role,
        tshirtSize: player?.tshirtSize,
        pantSize: player?.pantSize
      };

      if (player?.dbId) {
        // Update existing player
        await registrationService?.updatePlayer(player?.dbId, playerData);
        
        const fullName = [player?.firstName, player?.middleName, player?.lastName]
          .filter(Boolean).join(' ').trim();
        const updatedPlayers = formData?.players?.map(p => {
          if (p?.id === playerId) {
            return { ...player, name: fullName };
          }
          return p;
        });
        onChange('players', updatedPlayers);
      } else {
        // Add new player
        const savedPlayer = await registrationService?.addPlayer(registrationId, playerData);
        
        const fullName = [player?.firstName, player?.middleName, player?.lastName]
          .filter(Boolean).join(' ').trim();
        const updatedPlayers = formData?.players?.map(p =>
          p?.id === playerId ? { ...player, dbId: savedPlayer?.id, name: fullName } : p
        );
        onChange('players', updatedPlayers);
        onPlayerAdded?.();
      }
    } catch (error) {
      console.error('Error saving player:', error);
      // Show user-friendly error message with duplicate field details
      let errorMessage = 'Failed to save player. ';
      
      if (error?.message) {
        if (error.message.includes('unique') || error.message.includes('duplicate')) {
          // Parse which fields are duplicates from the error message
          const duplicateFields = [];
          
          // Check for mobile number duplicate
          if (error.message.includes('mobile_number') || error.message.includes('mobile')) {
            duplicateFields.push('Mobile Number');
          }
          
          // Check for email duplicate
          if (error.message.includes('email') || error.message.includes('email_key')) {
            duplicateFields.push('Email Address');
          }
          
          // Check for MCA ID duplicate (if it has a unique constraint)
          if (error.message.includes('mca_id') || error.message.includes('mca')) {
            duplicateFields.push('MCA ID');
          }
          
          // Build the error message
          if (duplicateFields.length > 0) {
            errorMessage += `The following field(s) already exist: ${duplicateFields.join(', ')}. Please use different values.`;
          } else {
            errorMessage += 'This player may already exist or have duplicate information.';
          }
        } else if (error.message.includes('security')) {
          errorMessage += 'Permission denied. Please try again.';
        } else {
          errorMessage += 'Please check all fields and try again.';
        }
      } else {
        errorMessage += 'Please try again.';
      }
      alert(errorMessage);
      
      // Refresh player data after error
      try {
        if (player?.dbId) {
          // For existing players, reload from database
          const players = await registrationService?.getPlayersByRegistration(registrationId);
          const refreshedPlayers = players?.map(p => ({
            id: p?.id,
            dbId: p?.id,
            name: `${p?.first_name} ${p?.middle_name || ''} ${p?.last_name}`.trim(),
            firstName: p?.first_name,
            middleName: p?.middle_name,
            lastName: p?.last_name,
            dob: p?.date_of_birth,
            mobile: p?.mobile_number,
            email: p?.email,
            mcaId: p?.mca_id,
            role: p?.role,
            tshirtSize: p?.tshirt_size,
            pantSize: p?.pant_size,
            isCaptain: p?.is_captain
          })) || [];
          
          // Find the player that had the error and restore it
          const errorPlayer = refreshedPlayers.find(p => p?.dbId === player?.dbId);
          if (errorPlayer) {
            const updatedPlayers = formData?.players?.map(p =>
              p?.id === playerId ? errorPlayer : p
            );
            onChange('players', updatedPlayers);
          }
        } else {
          // For new players, clear the duplicate fields
          const duplicateFields = [];
          if (error?.message?.includes('mobile_number') || error?.message?.includes('mobile')) {
            duplicateFields.push('mobile');
          }
          if (error?.message?.includes('email') || error?.message?.includes('email_key')) {
            duplicateFields.push('email');
          }
          if (error?.message?.includes('mca_id') || error?.message?.includes('mca')) {
            duplicateFields.push('mcaId');
          }
          
          // Clear only the duplicate fields, keep other data
          const updatedPlayers = formData?.players?.map(p => {
            if (p?.id === playerId) {
              const clearedPlayer = { ...p };
              if (duplicateFields.includes('mobile')) {
                clearedPlayer.mobile = '';
              }
              if (duplicateFields.includes('email')) {
                clearedPlayer.email = '';
              }
              if (duplicateFields.includes('mcaId')) {
                clearedPlayer.mcaId = '';
              }
              return clearedPlayer;
            }
            return p;
          });
          onChange('players', updatedPlayers);
        }
      } catch (refreshError) {
        console.error('Error refreshing player data:', refreshError);
        // If refresh fails, at least clear the duplicate fields for new players
        if (!player?.dbId) {
          const updatedPlayers = formData?.players?.map(p => {
            if (p?.id === playerId) {
              return {
                ...p,
                mobile: '',
                email: '',
                mcaId: ''
              };
            }
            return p;
          });
          onChange('players', updatedPlayers);
        }
      }
    } finally {
      setIsSavingPlayer(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border pb-4">
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-primary font-semibold">3</span>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground mb-1">Player Registration</h3>
              <p className="text-sm text-muted-foreground">
                Add 11 to 15 players with complete details. Click Save to persist each player's information.
              </p>
              {/* Show count of unsaved players */}
              {formData?.players?.some(p => !p?.dbId) && (
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <Icon name="AlertCircle" size={16} className="text-warning" />
                  <span className="text-warning font-medium">
                    {formData?.players?.filter(p => !p?.dbId).length} player(s) not saved yet
                  </span>
                </div>
              )}
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-primary">{formData?.players?.length}</p>
              <p className="text-xs text-muted-foreground">of 11-15 players</p>
            </div>
          </div>
        </div>

        {/* Quick Navigation */}
        {formData?.players?.length > 5 && (
          <div className="mt-3 relative">
            <Button
              variant="outline"
              size="sm"
              iconName="List"
              onClick={() => setShowPlayerList(!showPlayerList)}
              className="w-full"
            >
              Quick Jump to Player
            </Button>
            {showPlayerList && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto z-20">
                {formData?.players?.map((player, index) => {
                  const playerName = [player?.firstName, player?.lastName].filter(Boolean).join(' ') || `Player ${index + 1}`;
                  return (
                    <button
                      key={player?.id}
                      onClick={() => scrollToPlayer(player?.id)}
                      className="w-full text-left px-4 py-2 hover:bg-muted flex items-center justify-between border-b border-border last:border-b-0"
                    >
                      <span className="flex items-center gap-2">
                        <span className="text-sm font-medium">Player {index + 1}</span>
                        {player?.firstName && (
                          <span className="text-xs text-muted-foreground">
                            - {playerName}
                          </span>
                        )}
                      </span>
                      {player?.dbId ? (
                        <Icon name="CheckCircle2" size={16} className="text-green-600" />
                      ) : (
                        <Icon name="AlertCircle" size={16} className="text-warning" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="space-y-4">
        {formData?.players?.map((player, index) => {
          const isUnsaved = !player?.dbId;
          const isExpanded = expandedPlayers.has(player?.id);
          const playerName = [player?.firstName, player?.lastName].filter(Boolean).join(' ') || `Player ${index + 1}`;
          
          return (
            <div 
              key={player?.id}
              ref={el => playerRefs.current[player?.id] = el}
              className={`bg-card rounded-lg border p-6 space-y-4 ${
                isUnsaved 
                  ? 'border-warning/50 border-dashed' 
                  : 'border-border'
              }`}
            >
              {/* Collapsible Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3 flex-1">
                  <button
                    onClick={() => togglePlayerExpanded(player?.id)}
                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                  >
                    <Icon 
                      name={isExpanded ? "ChevronDown" : "ChevronRight"} 
                      size={20} 
                      className="text-muted-foreground"
                    />
                    <h4 className="text-base font-semibold text-foreground flex items-center gap-2">
                      <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </span>
                      {playerName}
                      {player?.dbId && (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Icon name="CheckCircle2" size={14} className="text-green-600" />
                          Saved
                        </span>
                      )}
                    </h4>
                  </button>
                </div>
              <div className="flex items-center gap-2">
                <Button
                  variant={player?.dbId ? "outline" : "default"}
                  size="sm"
                  iconName={player?.dbId ? "Save" : "Plus"}
                  onClick={() => handleSavePlayer(player?.id)}
                  disabled={isSavingPlayer === player?.id}
                >
                  {isSavingPlayer === player?.id 
                    ? 'Saving...' 
                    : player?.dbId 
                      ? 'Update' 
                      : 'Save'}
                </Button>
                {formData?.players?.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trash2"
                    onClick={() => handleRemovePlayer(player?.id)}
                    disabled={isDeletingPlayer === player?.id || isSavingPlayer === player?.id}
                  >
                    {isDeletingPlayer === player?.id ? 'Removing...' : 'Remove'}
                  </Button>
                )}
              </div>
            </div>

            {/* Collapsible Content */}
            {isExpanded && (
              <>
                {/* Warning banner for unsaved players */}
                {isUnsaved && (
                  <div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <Icon name="AlertTriangle" size={18} className="text-warning flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-warning-foreground mb-1">
                          This player record is not saved to the database yet
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Click the <strong>"Save"</strong> button above to persist this player's information. 
                          Unsaved data may be lost if you navigate away or refresh the page.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="First Name"
                type="text"
                placeholder="Enter first name"
                value={player?.firstName || ''}
                onChange={(e) => handlePlayerChange(player?.id, 'firstName', e?.target?.value)}
                required
                error={playerErrors[player?.id]?.firstName}
              />

              <Input
                label="Middle Name"
                type="text"
                placeholder="Enter middle name (optional)"
                value={player?.middleName || ''}
                onChange={(e) => handlePlayerChange(player?.id, 'middleName', e?.target?.value)}
              />

              <Input
                label="Last Name"
                type="text"
                placeholder="Enter last name"
                value={player?.lastName || ''}
                onChange={(e) => handlePlayerChange(player?.id, 'lastName', e?.target?.value)}
                required
                error={playerErrors[player?.id]?.lastName}
              />

              <Input
                label="Date of Birth"
                type="date"
                description="DD/MM/YYYY format"
                value={player?.dob || ''}
                onChange={(e) => handlePlayerChange(player?.id, 'dob', e?.target?.value)}
                required
                error={playerErrors[player?.id]?.dob}
              />

              <Input
                label="Mobile Number"
                type="tel"
                placeholder="9876543210"
                description="Must be unique across all players"
                value={player?.mobile || ''}
                onChange={(e) => handlePlayerChange(player?.id, 'mobile', e?.target?.value)}
                maxLength={10}
                required
                error={playerErrors[player?.id]?.mobile}
              />

              <Input
                label="Email Address"
                type="email"
                placeholder="player@example.com"
                description="Must be unique across all players"
                value={player?.email || ''}
                onChange={(e) => handlePlayerChange(player?.id, 'email', e?.target?.value)}
                required
                error={playerErrors[player?.id]?.email}
              />

              <Input
                label="MCA ID"
                type="text"
                placeholder="Enter MCA registration ID (optional)"
                value={player?.mcaId || ''}
                onChange={(e) => handlePlayerChange(player?.id, 'mcaId', e?.target?.value)}
                error={playerErrors[player?.id]?.mcaId}
              />

              <Select
                label="Player Role"
                placeholder="Select role"
                options={roleOptions}
                value={player?.role || ''}
                onChange={(value) => handlePlayerChange(player?.id, 'role', value)}
                required
                error={playerErrors[player?.id]?.role}
              />

              <Select
                label="T-Shirt Size"
                placeholder="Select size"
                options={tshirtSizes}
                value={player?.tshirtSize || ''}
                onChange={(value) => handlePlayerChange(player?.id, 'tshirtSize', value)}
                required
                error={playerErrors[player?.id]?.tshirtSize}
              />

              <Select
                label="Pant Size"
                placeholder="Select size"
                options={pantSizes}
                value={player?.pantSize || ''}
                onChange={(value) => handlePlayerChange(player?.id, 'pantSize', value)}
                required
                error={playerErrors[player?.id]?.pantSize}
              />
                </div>
              </>
            )}

            {/* Collapsed Summary View */}
            {!isExpanded && (
              <div className="text-sm text-muted-foreground pl-11 space-x-4">
                {player?.mobile && <span>Mobile: {player.mobile}</span>}
                {player?.email && <span>Email: {player.email}</span>}
                {player?.role && <span>Role: {player.role}</span>}
              </div>
            )}
          </div>
          );
        })}
      </div>

      {/* Add Player Button - Always visible in content */}
      {formData?.players?.length < 15 && (
        <div className="flex justify-center my-6">
          <Button
            variant="default"
            size="lg"
            iconName="Plus"
            iconPosition="left"
            onClick={handleAddPlayer}
            disabled={
              formData?.players?.length >= 15 || 
              isAddingPlayer || 
              (formData?.players?.length > 0 && !formData?.players[formData?.players?.length - 1]?.dbId)
            }
            className="min-w-[240px]"
          >
            {isAddingPlayer ? 'Adding Player...' : `Add Player (${formData?.players?.length}/15)`}
          </Button>
        </div>
      )}

      {/* Floating Action Buttons */}
      <div className="fixed bottom-24 right-6 flex flex-col gap-3 z-30">
        {showScrollTop && (
          <Button
            variant="default"
            size="lg"
            iconName="ArrowUp"
            onClick={scrollToTop}
            className="rounded-full shadow-lg w-14 h-14"
            title="Scroll to top"
          />
        )}
        {formData?.players?.length < 15 && (
          <Button
            variant="default"
            size="lg"
            iconName="Plus"
            onClick={handleAddPlayer}
            disabled={
              formData?.players?.length >= 15 || 
              isAddingPlayer || 
              (formData?.players?.length > 0 && !formData?.players[formData?.players?.length - 1]?.dbId)
            }
            className="rounded-full shadow-lg w-14 h-14"
            title={`Add new player (${formData?.players?.length}/15)`}
          />
        )}
      </div>

      {/* Warning message for unsaved last player */}
      {formData?.players?.length > 0 && !formData?.players[formData?.players?.length - 1]?.dbId && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex gap-3">
            <Icon name="AlertCircle" size={20} className="text-blue-600" />
            <div>
              <p className="text-sm font-medium text-blue-900 mb-1">
                Please save Player {formData?.players?.length} before adding a new player
              </p>
              <p className="text-xs text-blue-700">
                Click the Save button to persist the current player's information.
              </p>
            </div>
          </div>
        </div>
      )}

      {formData?.players?.length < 11 && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
          <div className="flex gap-3">
            <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
            <div>
              <p className="text-sm font-medium text-warning-foreground">
                Minimum {11 - formData?.players?.length} more player(s) required
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                You must register at least 11 players to proceed to the next step.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerRegistrationStep;
